import { useState, useEffect, useRef, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { clientService } from '@core/supabase/services/clientService';
import { ContextAiAssistModal, AiAssistIconButton } from '@shared/components/ai/ContextAiAssistModal';
import { Client } from '@core/types';

/** Module-level: avoids remounting inputs every parent render (one-character typing). */
function ClientFormRow({
  label,
  children,
  wide = false,
}: {
  label: ReactNode;
  children: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <>
      <dt className={`vscode-text-secondary pr-2 min-w-0 flex items-center ${wide ? 'lg:col-span-1' : ''}`}>{label}</dt>
      <dd className={`vscode-text-primary min-w-0 break-words ${wide ? 'lg:col-span-3' : ''}`}>{children}</dd>
    </>
  );
}

function ClientFormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <>
      <div className="ggj-profile-section-h">{title}</div>
      <div className="ggj-profile-section-body">
        <dl className="ggj-attr-grid">{children}</dl>
      </div>
    </>
  );
}

export const CLIENT_INDUSTRY_OPTIONS = [
  'Information Technology', 'Banking & Finance', 'Healthcare', 'Manufacturing',
  'Retail & E-commerce', 'Consulting', 'Education', 'Telecom', 'Real Estate', 'Other',
] as const;

export interface ClientFormProps {
  mode: 'new' | 'edit';
  client?: Client | null;
  readOnly?: boolean;
  onSave: (client: Client) => void;
  onDelete?: () => void;
  orgId: string;
}

export function ClientForm({
  mode,
  client,
  readOnly = false,
  onSave,
  onDelete,
  orgId,
}: ClientFormProps) {
  const disabled = readOnly;
  const submitLockRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [clientAiField, setClientAiField] = useState<null | 'terms' | 'notes'>(null);

  const [form, setForm] = useState({
    name: client?.name ?? '',
    domain: client?.domain ?? '',
    contact_name: client?.contact_name ?? '',
    contact_email: client?.contact_email ?? '',
    contact_phone: client?.contact_phone ?? '',
    industry: client?.industry ?? '',
    notes: client?.notes ?? '',
    commercial_agreement_label: client?.commercial_agreement_label ?? '',
    mou_reference: client?.mou_reference ?? '',
    contract_start_date: client?.contract_start_date?.slice(0, 10) ?? '',
    contract_end_date: client?.contract_end_date?.slice(0, 10) ?? '',
    commercial_terms_summary: client?.commercial_terms_summary ?? '',
  });

  useEffect(() => {
    if (client) {
      setForm({
        name: client.name ?? '',
        domain: client.domain ?? '',
        contact_name: client.contact_name ?? '',
        contact_email: client.contact_email ?? '',
        contact_phone: client.contact_phone ?? '',
        industry: client.industry ?? '',
        notes: client.notes ?? '',
        commercial_agreement_label: client.commercial_agreement_label ?? '',
        mou_reference: client.mou_reference ?? '',
        contract_start_date: client.contract_start_date?.slice(0, 10) ?? '',
        contract_end_date: client.contract_end_date?.slice(0, 10) ?? '',
        commercial_terms_summary: client.commercial_terms_summary ?? '',
      });
    }
  }, [client?.id]);

  const set = (field: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitLockRef.current) return;
    if (!form.name.trim()) { setError('Client name is required'); return; }
    if (mode === 'new' && !form.contact_email.trim()) { setError('Contact email is required — it is used for client approval emails during job publishing.'); return; }

    submitLockRef.current = true;
    setLoading(true);
    setError('');
    try {
      if (mode === 'new') {
        const created = await clientService.create({
          org_id: orgId,
          name: form.name.trim(),
          domain: form.domain || null,
          contact_name: form.contact_name || null,
          contact_email: form.contact_email || null,
          contact_phone: form.contact_phone || null,
          industry: form.industry || null,
          default_masking_template_id: null,
          notes: form.notes || null,
          commercial_agreement_label: form.commercial_agreement_label.trim() || null,
          mou_reference: form.mou_reference.trim() || null,
          contract_start_date: form.contract_start_date || null,
          contract_end_date: form.contract_end_date || null,
          commercial_terms_summary: form.commercial_terms_summary.trim() || null,
        });
        onSave(created);
      } else if (client) {
        const updated = await clientService.update(client.id, {
          name: form.name.trim(),
          domain: form.domain || null,
          contact_name: form.contact_name || null,
          contact_email: form.contact_email || null,
          contact_phone: form.contact_phone || null,
          industry: form.industry || null,
          notes: form.notes || null,
          commercial_agreement_label: form.commercial_agreement_label.trim() || null,
          mou_reference: form.mou_reference.trim() || null,
          contract_start_date: form.contract_start_date || null,
          contract_end_date: form.contract_end_date || null,
          commercial_terms_summary: form.commercial_terms_summary.trim() || null,
        });
        onSave(updated);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setLoading(false);
      submitLockRef.current = false;
    }
  };

  return (
    <>
    <form id="client-form" onSubmit={handleSubmit}>
      <ClientFormSection title="Company">
        <ClientFormRow label="Company Name *">
          <input className="input w-full max-w-md" placeholder="Infosys Ltd." value={form.name} onChange={set('name')} disabled={disabled} />
        </ClientFormRow>
        <ClientFormRow label="Website Domain">
          <input className="input w-full max-w-md" placeholder="infosys.com" value={form.domain} onChange={set('domain')} disabled={disabled} />
        </ClientFormRow>
        <ClientFormRow label="Industry">
          <select className="input w-full max-w-md" value={form.industry} onChange={set('industry')} disabled={disabled}>
            <option value="">Select industry</option>
            {CLIENT_INDUSTRY_OPTIONS.map((i) => (
              <option key={i} value={i}>{i}</option>
            ))}
          </select>
        </ClientFormRow>
      </ClientFormSection>

      <ClientFormSection title="Primary Contact">
        <ClientFormRow label="Contact Name">
          <input className="input w-full max-w-md" placeholder="Priya Sharma" value={form.contact_name} onChange={set('contact_name')} disabled={disabled} />
        </ClientFormRow>
        <ClientFormRow label={
          <span className="inline-flex items-center gap-1">
            Contact Email {mode === 'new' && <span className="text-red-500 font-bold">*</span>}
          </span>
        }>
          <div className="space-y-1 max-w-md">
            <input
              className="input w-full"
              type="email"
              placeholder="priya@infosys.com"
              value={form.contact_email}
              onChange={set('contact_email')}
              disabled={disabled}
              required={mode === 'new'}
            />
            {mode === 'new' && !form.contact_email.trim() && (
              <p className="text-[11px] text-amber-500 flex items-center gap-1">
                Required — used for client approval emails when publishing jobs.
              </p>
            )}
          </div>
        </ClientFormRow>
        <ClientFormRow label="Contact Phone">
          <input className="input w-full max-w-md" placeholder="+91 98765 43210" value={form.contact_phone} onChange={set('contact_phone')} disabled={disabled} />
        </ClientFormRow>
      </ClientFormSection>

      <ClientFormSection title="Agreement &amp; contract (preserved)">
        <ClientFormRow label="Commercial agreement">
          <input
            className="input w-full max-w-md"
            placeholder="e.g. MSA-2026, Master SOW"
            value={form.commercial_agreement_label}
            onChange={set('commercial_agreement_label')}
            disabled={disabled}
          />
        </ClientFormRow>
        <ClientFormRow label="MOU reference">
          <input
            className="input w-full max-w-md"
            placeholder="MOU title or file reference"
            value={form.mou_reference}
            onChange={set('mou_reference')}
            disabled={disabled}
          />
        </ClientFormRow>
        <ClientFormRow label="Contract start">
          <input className="input w-full max-w-md" type="date" value={form.contract_start_date} onChange={set('contract_start_date')} disabled={disabled} />
        </ClientFormRow>
        <ClientFormRow label="Contract end / review">
          <input className="input w-full max-w-md" type="date" value={form.contract_end_date} onChange={set('contract_end_date')} disabled={disabled} />
        </ClientFormRow>
        <ClientFormRow
          label={
            <span className="inline-flex items-center gap-2 flex-wrap">
              <span>Terms summary</span>
              {!disabled && (
                <AiAssistIconButton onClick={() => setClientAiField('terms')} title="AI assist — commercial terms summary" />
              )}
            </span>
          }
          wide={true}
        >
          <textarea
            className="input w-full resize-y min-h-[88px]"
            rows={4}
            placeholder="Short summary of fees, notice period, exclusivity, or renewal — for audit trail. Store executed PDFs in your DMS; link or reference here."
            value={form.commercial_terms_summary}
            onChange={set('commercial_terms_summary')}
            disabled={disabled}
          />
        </ClientFormRow>
      </ClientFormSection>

      <ClientFormSection title="Notes">
        <ClientFormRow
          label={
            <span className="inline-flex items-center gap-2 flex-wrap">
              <span>Notes</span>
              {!disabled && (
                <AiAssistIconButton onClick={() => setClientAiField('notes')} title="AI assist — client notes" />
              )}
            </span>
          }
          wide={true}
        >
          <textarea className="input w-full resize-none" rows={3} placeholder="Internal notes about this client..." value={form.notes} onChange={set('notes')} disabled={disabled} />
        </ClientFormRow>
      </ClientFormSection>

      {error && (
        <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded-lg px-3 py-2 my-4">{error}</p>
      )}

      {mode === 'new' && (
        <div className="flex gap-2 flex-wrap">
          <Link to="/clients" className="btn-secondary">[D] Discard</Link>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : '[S] Save'}
          </button>
        </div>
      )}
    </form>

    {clientAiField === 'terms' && (
      <ContextAiAssistModal
        open
        onClose={() => setClientAiField(null)}
        orgId={orgId}
        feature="ai_assist_client_commercial_terms"
        preset="generic_plain"
        title="AI — commercial terms summary"
        contextHint={`Client: ${form.name}\nAgreement ref: ${form.commercial_agreement_label || '—'}`}
        original={form.commercial_terms_summary}
        onApply={(text, mode) =>
          setForm((f) => ({
            ...f,
            commercial_terms_summary:
              mode === 'replace'
                ? text
                : f.commercial_terms_summary.trim()
                  ? `${f.commercial_terms_summary.trim()}\n\n${text}`
                  : text,
          }))
        }
      />
    )}
    {clientAiField === 'notes' && (
      <ContextAiAssistModal
        open
        onClose={() => setClientAiField(null)}
        orgId={orgId}
        feature="ai_assist_client_notes"
        preset="client_notes"
        title="AI — client notes"
        contextHint={`Client: ${form.name}\nIndustry: ${form.industry || '—'}`}
        original={form.notes}
        onApply={(text, mode) =>
          setForm((f) => ({
            ...f,
            notes:
              mode === 'replace'
                ? text
                : f.notes.trim()
                  ? `${f.notes.trim()}\n\n${text}`
                  : text,
          }))
        }
      />
    )}
    </>
  );
}
